import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import api from '../utils/api';

export default function Login() {
  const { login } = useAuth();
  const navigate   = useNavigate();
  const [email, setEmail]       = useState(() => localStorage.getItem('saved_email') || '');
  const [pw, setPw]             = useState('');
  const [remember, setRemember] = useState(() => !!localStorage.getItem('saved_email'));
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [maintenance, setMaint] = useState(null);
  const [expired, setExpired]   = useState(false);

  useEffect(() => {
    if (localStorage.getItem('session_expired')) {
      setExpired(true);
      localStorage.removeItem('session_expired');
    }
    api.get('/admin/maintenance').then(r => {
      if (r.data?.active) setMaint(r.data);
    }).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (remember) {
        localStorage.setItem('saved_email', email.trim());
      } else {
        localStorage.removeItem('saved_email');
      }
      await login(email.trim(), pw, remember);
      navigate('/dashboard');
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message;
      if (err.response?.data?.maintenance) {
        setError('Wartungsmodus aktiv — nur Administratoren können sich einloggen.');
      } else {
        setError(msg || 'Anmeldung fehlgeschlagen. Bitte E-Mail und Passwort prüfen.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100dvh',
      background: 'linear-gradient(160deg, #fef2f2 0%, var(--bg) 60%)',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: '24px 20px',
      paddingTop: 'calc(24px + env(safe-area-inset-top, 0px))',
      paddingBottom: 'calc(24px + env(safe-area-inset-bottom, 0px))',
    }}>

      {maintenance?.active && (
        <div style={{ width:'100%', maxWidth:420, marginBottom:16, padding:'12px 16px', background:'var(--yellow-bg)', borderRadius:12, border:'1px solid rgba(217,119,6,.25)' }}>
          <div style={{ fontWeight:700, fontSize:'.88rem', color:'var(--yellow)' }}>🔧 Wartungsmodus aktiv</div>
          <div style={{ fontSize:'.76rem', color:'var(--text-muted)', marginTop:3 }}>
            {maintenance.message} — <strong>Nur Administratoren</strong> können sich einloggen.
          </div>
        </div>
      )}

      {expired && (
        <div style={{ width:'100%', maxWidth:420, marginBottom:16, padding:'12px 16px', background:'var(--yellow-bg)', borderRadius:12, border:'1px solid rgba(217,119,6,.2)' }}>
          <div style={{ fontWeight:700, fontSize:'.85rem', color:'var(--yellow)' }}>⏰ Sitzung abgelaufen</div>
          <div style={{ fontSize:'.74rem', color:'var(--text-muted)', marginTop:2 }}>Sie wurden nach 8 Stunden automatisch abgemeldet.</div>
        </div>
      )}

      <div style={{ marginBottom:24, textAlign:'center', width:'100%', maxWidth:420 }}>
        <img src="/logo.jpg" alt="Getränkefachhandel Gerhards"
          style={{ width:'100%', height:160, objectFit:'cover', borderRadius:16, boxShadow:'0 8px 32px rgba(0,0,0,.12)' }}
          onError={e => e.target.style.display='none'} />
        <div style={{ marginTop:12, fontSize:'.72rem', color:'var(--text-muted)', fontWeight:700, letterSpacing:'.1em', textTransform:'uppercase' }}>
          Fuhrparkverwaltung
        </div>
      </div>

      <div className="card" style={{ width:'100%', maxWidth:420 }}>
        <div style={{ padding:'22px 22px 0' }}>
          <h1 style={{ fontSize:'1.25rem', marginBottom:4 }}>Willkommen</h1>
          <p style={{ color:'var(--text-muted)', fontSize:'.83rem', marginBottom:20 }}>Bitte melden Sie sich an</p>
        </div>

        <form onSubmit={submit} style={{ padding:'0 22px 22px', display:'flex', flexDirection:'column', gap:13 }}>
          {error && (
            <div style={{ background:'var(--red-bg)', color:'var(--red)', borderRadius:9, padding:'10px 14px', fontSize:'.82rem', fontWeight:600, border:'1px solid rgba(220,38,38,.2)' }}>
              ⚠️ {error}
            </div>
          )}

          <div className="f-group">
            <label className="f-label">E-Mail-Adresse</label>
            <input className="f-input" type="email" value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="ihre@email.de" required
              autoCapitalize="none" autoCorrect="off" autoComplete="email"
              disabled={loading} style={{ fontSize:16 }} />
          </div>

          <div className="f-group">
            <label className="f-label">Passwort</label>
            <input className="f-input" type="password" value={pw}
              onChange={e => setPw(e.target.value)}
              placeholder="••••••••" required
              autoComplete="current-password"
              disabled={loading} style={{ fontSize:16 }} />
          </div>

          {/* Anmeldedaten merken */}
          <div style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer', userSelect:'none' }}
            onClick={() => setRemember(!remember)}>
            <div style={{
              width:20, height:20, borderRadius:5, flexShrink:0,
              border:`2px solid ${remember ? 'var(--accent)' : 'var(--border)'}`,
              background: remember ? 'var(--accent)' : 'transparent',
              display:'flex', alignItems:'center', justifyContent:'center',
              transition:'all 150ms',
            }}>
              {remember && <span style={{ color:'#fff', fontSize:'.8rem', fontWeight:800, lineHeight:1 }}>✓</span>}
            </div>
            <div>
              <div style={{ fontSize:'.85rem', fontWeight:600 }}>Anmeldedaten merken</div>
              <div style={{ fontSize:'.7rem', color:'var(--text-muted)' }}>
                {remember ? 'Dauerhaft eingeloggt bleiben' : 'Automatische Abmeldung nach 8 Stunden'}
              </div>
            </div>
          </div>

          <button type="submit" className="btn btn-primary" disabled={loading}
            style={{ padding:'14px', fontSize:'1rem', borderRadius:10, justifyContent:'center', marginTop:2 }}>
            {loading
              ? <><span className="spinner" style={{ width:18, height:18, borderWidth:2 }}/> Anmelden...</>
              : 'Anmelden'}
          </button>
        </form>
      </div>

      <p style={{ marginTop:20, fontSize:'.7rem', color:'var(--text-faint)', textAlign:'center', lineHeight:1.8 }}>
        Getränkefachhandel Gerhards · Bruch 1 · 41334 Nettetal<br/>Tel: 02157 / 13 05 92
      </p>
    </div>
  );
}
